// src/modules/Admin/pages/InvestorGoals/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  FaBullseye,
  FaPlus,
  FaTimes,
  FaEdit,
  FaTrash,
  FaCheck,
  FaSpinner
} from 'react-icons/fa';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #111;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #333;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const GoalItem = styled.div`
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: flex-start;
  gap: 16px;

  &:last-child {
    border-bottom: none;
  }
`;

const GoalInfo = styled.div`
  flex: 1;
`;

const GoalTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111;
  margin-bottom: 4px;
`;

const GoalMeta = styled.div`
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
`;

const GoalValues = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;

const GoalProgress = styled.div`
  flex: 1;
  max-width: 200px;
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;

  .fill {
    height: 100%;
    background: ${props => {
      const pct = props.$percent || 0;
      if (pct >= 100) return '#10b981';
      if (pct >= 75) return '#3b82f6';
      if (pct >= 50) return '#f59e0b';
      return '#ef4444';
    }};
    width: ${props => Math.min(props.$percent || 0, 100)}%;
  }
`;

const GoalPercent = styled.span`
  font-weight: 600;
  color: ${props => {
    const pct = props.$percent || 0;
    if (pct >= 100) return '#10b981';
    if (pct >= 75) return '#3b82f6';
    if (pct >= 50) return '#f59e0b';
    return '#ef4444';
  }};
`;

const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.$status) {
      case 'achieved': return 'rgba(16, 185, 129, 0.1)';
      case 'missed': return 'rgba(239, 68, 68, 0.1)';
      default: return 'rgba(59, 130, 246, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'achieved': return '#10b981';
      case 'missed': return '#ef4444';
      default: return '#3b82f6';
    }
  }};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: ${props => props.$danger ? 'rgba(239, 68, 68, 0.1)' : '#f5f5f5'};
  color: ${props => props.$danger ? '#ef4444' : '#666'};
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: ${props => props.$danger ? 'rgba(239, 68, 68, 0.2)' : '#e5e5e5'};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111;
`;

const ModalClose = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;

  &:hover {
    background: #e5e5e5;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  input, select, textarea {
    width: 100%;
    padding: 12px 14px;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #111;
    }
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  color: #666;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 24px;
  background: #111;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
`;

const CATEGORIES = [
  { value: 'revenue', label: 'Receita' },
  { value: 'growth', label: 'Crescimento' },
  { value: 'clients', label: 'Clientes' },
  { value: 'milestone', label: 'Marco' },
  { value: 'market', label: 'Mercado' },
  { value: 'other', label: 'Outro' }
];

const STATUS_OPTIONS = [
  { value: 'in_progress', label: 'Em Andamento' },
  { value: 'achieved', label: 'Atingida' },
  { value: 'missed', label: 'Nao Atingida' },
  { value: 'adjusted', label: 'Ajustada' }
];

const InvestorGoals = () => {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'revenue',
    target_value: '',
    current_value: '0',
    unit: 'R$',
    period_type: 'yearly',
    period_start: new Date().toISOString().split('T')[0],
    period_end: `${new Date().getFullYear()}-12-31`,
    status: 'in_progress',
    notes: ''
  });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const { data, error } = await supabase
        .from('investor_goals')
        .select('*')
        .order('created_at', { ascending: false });

      if (!error) {
        setGoals(data || []);
      }
    } catch (error) {
      console.warn('Error fetching goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingGoal(null);
    setFormData({
      title: '',
      description: '',
      category: 'revenue',
      target_value: '',
      current_value: '0',
      unit: 'R$',
      period_type: 'yearly',
      period_start: new Date().toISOString().split('T')[0],
      period_end: `${new Date().getFullYear()}-12-31`,
      status: 'in_progress',
      notes: ''
    });
    setShowModal(true);
  };

  const openEditModal = (goal) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.title || '',
      description: goal.description || '',
      category: goal.category || 'revenue',
      target_value: goal.target_value?.toString() || '',
      current_value: goal.current_value?.toString() || '0',
      unit: goal.unit || 'R$',
      period_type: goal.period_type || 'yearly',
      period_start: goal.period_start || new Date().toISOString().split('T')[0],
      period_end: goal.period_end || `${new Date().getFullYear()}-12-31`,
      status: goal.status || 'in_progress',
      notes: goal.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.target_value) {
      alert('Titulo e valor meta sao obrigatorios');
      return;
    }

    setSaving(true);
    try {
      const goalData = {
        ...formData,
        target_value: parseFloat(formData.target_value) || 0,
        current_value: parseFloat(formData.current_value) || 0,
        created_by: user?.email || 'admin'
      };

      if (editingGoal) {
        const { error } = await supabase
          .from('investor_goals')
          .update(goalData)
          .eq('id', editingGoal.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('investor_goals')
          .insert([goalData]);

        if (error) throw error;
      }

      await fetchGoals();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving goal:', error);
      alert('Erro ao salvar meta: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta meta?')) return;

    try {
      const { error } = await supabase
        .from('investor_goals')
        .delete()
        .eq('id', goalId);

      if (error) throw error;
      await fetchGoals();
    } catch (error) {
      console.error('Error deleting goal:', error);
      alert('Erro ao excluir meta');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatValue = (value, unit) => {
    if (unit === 'R$') return formatCurrency(value);
    return `${value}${unit}`;
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <FaSpinner style={{ fontSize: 24, animation: 'spin 1s linear infinite' }} />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Metas com Investidores</Title>
        <AddButton onClick={openCreateModal}>
          <FaPlus />
          Nova Meta
        </AddButton>
      </Header>

      <Card>
        {goals.length === 0 ? (
          <EmptyState>
            <FaBullseye style={{ fontSize: 40, marginBottom: 16 }} />
            <p>Nenhuma meta cadastrada</p>
          </EmptyState>
        ) : (
          goals.map((goal) => {
            const percent = goal.target_value > 0 ? (goal.current_value / goal.target_value) * 100 : 0;
            return (
              <GoalItem key={goal.id}>
                <GoalInfo>
                  <GoalTitle>{goal.title}</GoalTitle>
                  <GoalMeta>
                    <span>{CATEGORIES.find(c => c.value === goal.category)?.label || goal.category}</span>
                    <span>{goal.period_start} a {goal.period_end}</span>
                  </GoalMeta>
                  <GoalValues>
                    <span style={{ fontWeight: 600 }}>{formatValue(goal.current_value, goal.unit)}</span>
                    <span style={{ color: '#999' }}>/ {formatValue(goal.target_value, goal.unit)}</span>
                    <GoalProgress $percent={percent}>
                      <div className="fill" />
                    </GoalProgress>
                    <GoalPercent $percent={percent}>{percent.toFixed(0)}%</GoalPercent>
                  </GoalValues>
                </GoalInfo>
                <StatusBadge $status={goal.status}>
                  {STATUS_OPTIONS.find(s => s.value === goal.status)?.label || goal.status}
                </StatusBadge>
                <Actions>
                  <IconButton onClick={() => openEditModal(goal)}>
                    <FaEdit />
                  </IconButton>
                  <IconButton $danger onClick={() => handleDelete(goal.id)}>
                    <FaTrash />
                  </IconButton>
                </Actions>
              </GoalItem>
            );
          })
        )}
      </Card>

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingGoal ? 'Editar Meta' : 'Nova Meta'}</ModalTitle>
              <ModalClose onClick={() => setShowModal(false)}>
                <FaTimes />
              </ModalClose>
            </ModalHeader>
            <form onSubmit={handleSubmit}>
              <ModalBody>
                <FormGroup>
                  <label>Titulo *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Receita Anual 2025"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label>Descricao</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descricao da meta..."
                    rows={2}
                  />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <label>Categoria</label>
                    <select
                      value={formData.category}
                      onChange={e => setFormData({ ...formData, category: e.target.value })}
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </FormGroup>
                  <FormGroup>
                    <label>Unidade</label>
                    <select
                      value={formData.unit}
                      onChange={e => setFormData({ ...formData, unit: e.target.value })}
                    >
                      <option value="R$">R$ (Reais)</option>
                      <option value="%">% (Percentual)</option>
                      <option value=" clientes"> Clientes</option>
                      <option value=" un"> Unidades</option>
                    </select>
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <label>Valor Meta *</label>
                    <input
                      type="number"
                      value={formData.target_value}
                      onChange={e => setFormData({ ...formData, target_value: e.target.value })}
                      placeholder="500000"
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Valor Atual</label>
                    <input
                      type="number"
                      value={formData.current_value}
                      onChange={e => setFormData({ ...formData, current_value: e.target.value })}
                      placeholder="0"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <label>Data Inicio</label>
                    <input
                      type="date"
                      value={formData.period_start}
                      onChange={e => setFormData({ ...formData, period_start: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Data Fim</label>
                    <input
                      type="date"
                      value={formData.period_end}
                      onChange={e => setFormData({ ...formData, period_end: e.target.value })}
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                  >
                    {STATUS_OPTIONS.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Observacoes</label>
                  <textarea
                    value={formData.notes}
                    onChange={e => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notas internas..."
                    rows={2}
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <CancelButton type="button" onClick={() => setShowModal(false)}>
                  Cancelar
                </CancelButton>
                <SubmitButton type="submit" disabled={saving}>
                  {saving ? 'Salvando...' : (editingGoal ? 'Atualizar' : 'Criar Meta')}
                </SubmitButton>
              </ModalFooter>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default InvestorGoals;
